import { useEffect } from "react";
import { Card, Typography, Divider, Row, Col, Spin, Empty, DatePicker, Table } from "antd";
import { CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { Controller, useForm, useWatch } from "react-hook-form";
import useAllStatistic from "api/dashboard/useAllStatistic";
import useTopCustomer from "api/dashboard/useTopCustomer";
import useTopSearch from "api/dashboard/useTopSearch";
import useAllRevenue from "api/dashboard/useAllRevenue";
import useTopProduct from "api/dashboard/useTopProduct";
import toKMBT from "helpers/toKMBT";
import dayjs from "dayjs";
import useCashflowStatistic from "api/dashboard/useCashflowStatistic";
import useUserSummary from "api/dashboard/useUserSummary";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const Superadmin = () => {
    const { control, setValue } = useForm<{
        dateRange: [dayjs.Dayjs, dayjs.Dayjs];
    }>({
        defaultValues: {
            dateRange: [dayjs().startOf('day'), dayjs().endOf('day')],
        },
    });

    const dateRange = useWatch({ control, name: "dateRange" }) as [dayjs.Dayjs, dayjs.Dayjs] | null;

    // Format dates for API
    const startDate = dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : null;
    const endDate = dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : null;

    // Fetch statistics based on the selected date range
    const { data: allStatistic, isFetching, refetch } = useAllStatistic({
        start_date: startDate,
        end_date: endDate
    });

    const { data: cashflowStat } = useCashflowStatistic({
        start_date: startDate,
        end_date: endDate
    });
    const {
        data: topCustomer,
        isFetching: isFetchTopCustomer,
        refetch: refetchTopCustomer
    } = useTopCustomer({
        start_date: startDate,
        end_date: endDate
    });
    const {
        data: topSearch,
        isFetching: isFetchTopSearch,
    } = useTopSearch();
    const {
        data: allRevenue,
        isFetching: isFetchAllRevenue,
        refetch: refetchAllRevenue
    } = useAllRevenue({
        start_date: startDate,
        end_date: endDate
    });
    const {
        data: topProduct,
        isFetching: isFetchTopProduct,
    } = useTopProduct();
    const {
        data: userSummary,
        isFetching: isFetchUserSummary,
    } = useUserSummary();

    const formattedRevenueData = allRevenue?.data?.data?.map((item) => {
        return {
            name: item.period,
            revenue: parseFloat(item.total_revenue),
        };
    }) || [];

    useEffect(() => {
        if (startDate && endDate) {
            refetch();
            refetchTopCustomer();
            refetchAllRevenue();
        }
    }, [startDate, endDate, refetch, refetchTopCustomer, refetchAllRevenue]);

    const statisticsData = [
        {
            title: "Total Views",
            value: allStatistic?.data?.data?.total_views,
            change: allStatistic?.data?.data?.views_change,
            previous: 'Total pengunjung website',
            isPositive: allStatistic?.data?.data?.views_change > 0,
        },
        {
            title: "Total New Users",
            value: allStatistic?.data?.data?.total_new_user,
            change: allStatistic?.data?.data?.new_user_change,
            previous: 'Total user baru',
            isPositive: allStatistic?.data?.data?.new_user_change > 0,
        },
        {
            title: "Total Orders",
            value: allStatistic?.data?.data?.total_order,
            change: allStatistic?.data?.data?.order_change,
            previous: 'Total pesanan pelanggan',
            isPositive: allStatistic?.data?.data?.order_change > 0,
        },
        {
            title: "Total Current User",
            value: allStatistic?.data?.data?.total_user,
            change: allStatistic?.data?.data?.user_change,
            previous: 'Total user yang sudah terdata',
            isPositive: allStatistic?.data?.data?.user_change > 0,
        },
    ];

    const userSummaryColumns = [
        {
            title: "Kecamatan",
            dataIndex: "district_name",
            key: "district_name",
        },
        {
            title: "Total Customer",
            dataIndex: "total_customer_per_district",
            key: "total_customer_per_district",
            align: "right" as const,
            render: (value: number) => value.toLocaleString("id-ID"),
        },
    ];


    // New summary statistics for B2C and B2B
    const summaryData = [
        {
            title: "Total Transaksi B2C",
            value: cashflowStat?.data?.data?.total_transaction_b2c || 0,
            subtitle: "Jumlah transaksi B2C",
            isCurrency: false,
        },
        {
            title: "Total Revenue B2C",
            value: cashflowStat?.data?.data?.total_revenue_b2c || 0,
            subtitle: "Pendapatan dari B2C",
            isCurrency: true,
        },
        {
            title: "Total Revenue B2B",
            value: cashflowStat?.data?.data?.total_revenue_b2b || 0,
            subtitle: "Pendapatan dari B2B",
            isCurrency: true,
        },
        {
            title: "Total Piutang B2B",
            value: cashflowStat?.data?.data?.total_piutang_b2b || 0,
            subtitle: "Piutang yang belum dibayar B2B",
            isCurrency: true,
        },
        {
            title: "Total Piutang B2C",
            value: cashflowStat?.data?.data?.total_piutang_b2c || 0,
            subtitle: "Piutang yang belum dibayar B2C",
            isCurrency: true,
        },
        {
            title: "Average Order B2C",
            value: cashflowStat?.data?.data?.average_order_b2c || 0,
            subtitle: "Rata-rata nilai pesanan B2C",
            isCurrency: true,
        },
    ];

    // Quick filter presets
    const rangePresets: {
        label: string;
        value: [dayjs.Dayjs, dayjs.Dayjs];
    }[] = [
            { label: 'Hari Ini', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
            { label: '7 Hari Terakhir', value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
            { label: '30 Hari Terakhir', value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
            { label: 'Bulan Ini', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
            { label: 'Bulan Lalu', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
        ];

    return (
        <div className="min-h-screen">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Overview Performance Section */}
                <Card className="border border-gray-200">
                    <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                        <Title level={3} className="!mb-0 text-gray-900">
                            Overview Performance
                        </Title>
                        <Controller
                            name="dateRange"
                            control={control}
                            render={({ field }) => (
                                <RangePicker
                                    {...field}
                                    format="DD MMM YYYY"
                                    placeholder={['Tanggal Mulai', 'Tanggal Akhir']}
                                    presets={rangePresets}
                                    onChange={(dates) => setValue("dateRange", dates)}
                                    allowClear={false}
                                    className="w-full sm:w-auto"
                                />
                            )}
                        />
                    </div>

                    {/* Statistics Grid */}
                    <Row gutter={[24, 24]}>
                        {statisticsData.map((item, index) => (
                            <Col key={index} xs={24} sm={12} lg={6}>
                                <div className="relative">
                                    {isFetching && (
                                        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10">
                                            <Spin size="small" />
                                        </div>
                                    )}
                                    <div className="p-4 border border-gray-200 rounded-lg bg-white">
                                        <div className="mb-3">
                                            <Text className="text-sm text-gray-600 font-medium">
                                                {item.title}
                                            </Text>
                                        </div>

                                        <div className="flex items-center justify-between mb-2">
                                            <Title level={2} className="!mb-0 text-gray-900">
                                                {item?.value?.toLocaleString() || "0"}
                                            </Title>

                                            <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${item.isPositive
                                                ? "bg-green-50 text-green-700"
                                                : "bg-red-50 text-red-700"
                                                }`}>
                                                {item.isPositive ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                                                {Math.abs(item.change) || 0}
                                            </div>
                                        </div>

                                        <Text className="text-xs text-gray-500">
                                            {item.previous}
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card>

                {/* B2C & B2B Summary Section */}
                <Card className="border border-gray-200">
                    <Title level={3} className="!mb-4 text-gray-900">
                        B2C & B2B Summary
                    </Title>
                    <Divider className="my-4" />

                    <Row gutter={[24, 24]}>
                        {summaryData.map((item, index) => (
                            <Col key={index} xs={24} sm={12} lg={8}>
                                <div className="relative">
                                    {isFetching && (
                                        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10">
                                            <Spin size="small" />
                                        </div>
                                    )}
                                    <div className="p-5 border border-gray-200 rounded-lg bg-gradient-to-br from-white to-gray-50">
                                        <div className="mb-2">
                                            <Text className="text-sm text-gray-600 font-medium">
                                                {item.title}
                                            </Text>
                                        </div>

                                        <Title level={2} className="!mb-2 text-gray-900">
                                            {item.isCurrency
                                                ? new Intl.NumberFormat("id-ID", {
                                                    style: "currency",
                                                    currency: "IDR",
                                                    minimumFractionDigits: 0,
                                                }).format(Number(item.value))
                                                : item.value.toLocaleString()
                                            }
                                        </Title>

                                        <Text className="text-xs text-gray-500">
                                            {item.subtitle}
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card>

                <div className="grid grid-cols-12 gap-6">
                    {/* Revenue Chart */}
                    <div className="col-span-12 lg:col-span-8">
                        <Card className="border border-gray-200">
                            <Title level={4} className="!mb-4 text-gray-900">
                                Total Pendapatan
                            </Title>
                            <Divider className="my-4" />

                            <div className="relative" style={{ height: "320px" }}>
                                {isFetchAllRevenue ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
                                        <Spin tip="Loading..." />
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={formattedRevenueData}>
                                            <defs>
                                                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                            <XAxis
                                                dataKey="name"
                                                stroke="#6b7280"
                                                fontSize={12}
                                            />
                                            <YAxis
                                                tickFormatter={(value) => toKMBT(value)}
                                                stroke="#6b7280"
                                                fontSize={12}
                                            />
                                            <Tooltip
                                                formatter={(value) =>
                                                    new Intl.NumberFormat("id-ID", {
                                                        style: "currency",
                                                        currency: "IDR",
                                                        minimumFractionDigits: 0,
                                                    }).format(Number(value))
                                                }
                                                contentStyle={{
                                                    backgroundColor: '#ffffff',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                                }}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="revenue"
                                                stroke="#10b981"
                                                strokeWidth={3}
                                                fill="url(#colorRevenue)"
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </Card>

                        <Divider className="my-6" />
                        <Title level={5} className="!mb-4">
                            Sebaran User per Kecamatan
                        </Title>

                        <Table
                            columns={userSummaryColumns}
                            dataSource={userSummary?.data?.data || []}
                            loading={isFetchUserSummary}
                            rowKey={(record) => record.district_name}
                            pagination={false}
                            size="middle"
                        />
                    </div>
                    {/* Right Side Lists */}
                    <div className="col-span-12 lg:col-span-4 space-y-6">
                        {/* Top Customer */}
                        <Card className="border border-gray-200">
                            <Title level={4} className="!mb-4 text-gray-900">
                                Top Customer
                            </Title>
                            <Divider className="my-4" />

                            <div className="relative min-h-[180px]">
                                {isFetchTopCustomer ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
                                        <Spin size="small" />
                                    </div>
                                ) : (
                                    <>
                                        {Array.isArray(topCustomer?.data?.data) && topCustomer?.data?.data?.length > 0 ? (
                                            <div className="space-y-3">
                                                {topCustomer.data.data.slice(0, 5).map((item, index) => (
                                                    <div key={index} className="flex justify-between items-center py-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-500 w-4">
                                                                {index + 1}.
                                                            </span>
                                                            <Text className="text-gray-900 font-medium">
                                                                {item.name}
                                                            </Text>
                                                        </div>
                                                        <Text className="text-gray-700 font-semibold">
                                                            {item.total_orders}
                                                        </Text>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <Empty
                                                description="No top customers available"
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </Card>

                        {/* Top Search */}
                        <Card className="border border-gray-200">
                            <Title level={4} className="!mb-4 text-gray-900">
                                Top Pencarian Pelanggan
                            </Title>
                            <Divider className="my-4" />

                            <div className="relative min-h-[180px]">
                                {isFetchTopSearch ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
                                        <Spin size="small" />
                                    </div>
                                ) : (
                                    <>
                                        {Array.isArray(topSearch?.data?.data) && topSearch?.data?.data?.length > 0 ? (
                                            <div className="space-y-3">
                                                {topSearch.data.data.slice(0, 5).map((item, index) => (
                                                    <div key={index} className="flex justify-between items-center py-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-500 w-4">
                                                                {index + 1}.
                                                            </span>
                                                            <Text className="text-gray-900 font-medium">
                                                                {item.keyword}
                                                            </Text>
                                                        </div>
                                                        <Text className="text-gray-700 font-semibold">
                                                            {item.search_count}
                                                        </Text>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <Empty
                                                description="No search data available"
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </Card>

                        {/* Top Product */}
                        <Card className="border border-gray-200">
                            <Title level={4} className="!mb-4 text-gray-900">
                                Top Produk
                            </Title>
                            <Divider className="my-4" />

                            <div className="relative min-h-[180px]">
                                {isFetchTopProduct ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
                                        <Spin size="small" />
                                    </div>
                                ) : (
                                    <>
                                        {Array.isArray(topProduct?.data?.data) && topProduct?.data?.data?.length > 0 ? (
                                            <div className="space-y-3">
                                                {topProduct.data.data.slice(0, 5).map((item, index) => (
                                                    <div key={index} className="flex justify-between items-center py-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-500 w-4">
                                                                {index + 1}.
                                                            </span>
                                                            <Text className="text-gray-900 font-medium">
                                                                {item.name}
                                                            </Text>
                                                        </div>
                                                        <Text className="text-gray-700 font-semibold">
                                                            {item.total_purchase}
                                                        </Text>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <Empty
                                                description="No top products available"
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Superadmin;